import { FC, Fragment, useEffect, useState } from "react";
import { Autocomplete, IconButton, TextField, Typography } from "@mui/material";
import {
  DropdownArrPropsType,
  IconIdType,
  SingleDropType
} from "DataTypes/Form";
import { CommonStyle } from "Components";
import InputLabel from "Components/ControlledForm/Input/Label";
import { ScreenInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import ImgPath from "Constant/Imgs";

const SingleApi: FC<SingleDropType> = (props) => {
  const {
    id,
    label,
    placeholder,
    error,
    errorText,
    required,
    readOnly,
    dropArr,
    inputChange,
    dropVal,
    onClick,
    loading,
    inputVal,
    enableFormLoad,
    iconSearch,
    onIconClick
  } = props;
  const value = dropArr?.find(findEl => findEl.value === dropVal);
  const newVal = value || null;
  const newInputVal = inputVal || "";
  const { formLoad }: ScreenInitialStateType = useSelector(
    (state: any) => state?.screen
  );
  const [localLoad, setLocalLoad] = useState(false);
  const [localInput, setLocalInput] = useState("");
  const [searchedVal, setSearchedVal] = useState("");
  const viewOnly = readOnly || (enableFormLoad ? formLoad : false);
  const loadingOnly =
    loading ||
    localLoad ||
    (enableFormLoad ? formLoad : false);

  useEffect(() => {
    setLocalInput(newVal?.label || "");
  }, [newVal])

  const handleIconClick = async (iconId: IconIdType): Promise<void> => {
    setLocalLoad(true);
    const newSearchedVal = iconId == "clear" ? "" : newInputVal.toLowerCase();
    if (onIconClick && (iconId == "clear" || newSearchedVal !== searchedVal)) {
      await onIconClick(iconId);
      setSearchedVal(newSearchedVal)
    }
    setLocalLoad(false);
  };

  return (
    <Fragment>
      {label && (
        <InputLabel label={label} required={required ? required : false} />
      )}
      <Autocomplete
        id={id}
        readOnly={viewOnly}
        options={dropArr || []}
        loading={loadingOnly}
        value={newVal}
        onChange={(
          event: React.SyntheticEvent,
          newValue: DropdownArrPropsType | null
        ) => {
          if (
            event?.type == "click" ||
            event?.type == "keydown" ||
            event?.type == "change"
          ) {
            onClick && onClick(newValue?.value || null);
            setSearchedVal(newValue?.label || "");
          }
        }}
        renderOption={(props, option) => (
          <Typography variant="subtitle1" {...props}>
            {option.label}
          </Typography>
        )}
        {...(iconSearch ?
          {
            filterOptions: (x) => x,
            onKeyDown: (event) => {
              if (event.key === "Enter") handleIconClick("search");
            }
          }
          : {})}
        inputValue={iconSearch ? newInputVal : localInput}
        onInputChange={(event, val, reason) => {
          if (reason !== "reset") {
            if (iconSearch) inputChange && inputChange(val);
            else setLocalInput(val)
          }
        }}
        disablePortal
        clearIcon={false}
        clearOnBlur={false}
        getOptionLabel={(option: DropdownArrPropsType) =>
          option.label ?? option
        }
        isOptionEqualToValue={(option, value) => option.id == value.id}
        fullWidth
        sx={{ p: "0px !important", fontSize: "0.85rem" }}
        renderInput={(params) => (
          <TextField
            placeholder={placeholder}
            error={error ? error : false}
            {...params}
            sx={{
              ...(readOnly ? { ...sxReadOnly } : { ...CommonStyle.sxWhiteBox }),
              "&>div": {
                p: "0px !important",
                fontSize: "0.85rem",
                "& >div": {
                  right: "0px !important",
                  position: "unset",
                  transform: "translate(0, 0%)",
                  display: "flex",
                  alignItems: "center",
                },
                "& >input": {
                  px: 0.5,
                  py: 1,
                },
                "& >fieldset": {
                  borderWidth: 0,
                },
              },
            }}
            InputProps={{
              ...params.InputProps,
              ...({
                endAdornment: (
                  <>
                    {(iconSearch && newInputVal) ?
                      <IconButton
                        title="Clear"
                        sx={{ ...sxInputIcon }}
                        onClick={() => handleIconClick("clear")}
                      >
                        <ImgPath.CloseIcon color="primary" />
                      </IconButton>
                      : <></>
                    }
                    {(iconSearch) ?
                      <IconButton
                        title="Search"
                        sx={{ ...sxInputIcon }}
                        onClick={() => handleIconClick("search")}
                      >
                        <ImgPath.SearchIcon color="primary" />
                      </IconButton>
                      : <></>
                    }
                    {params.InputProps.endAdornment}
                  </>
                )
              })
            }}
          />
        )}
      />
      {error && (
        <Typography variant="subtitle2" color="red">
          {errorText}
        </Typography>
      )}
    </Fragment>
  );
};
export default SingleApi;

const sxReadOnly = {
  backgroundColor: "custom.grey",
  boxShadow: "0px 0px 6px #00000029",
  borderRadius: 1.25,
  " >fieldset": {
    display: "none",
  },
};

const sxInputIcon = {
  p: 0.35,
  "&>svg": {
    width: "0.9em", height: "0.9em"
  }
}
//=> There will be 2 types of single select dropdown:

//=> Normal single select where
///=> Filter is inbuild and search in default options.
///=> Once the value is selected, its input value will be according to selected value label.
///=> Clear Icon and search not available

//=> Api single select where
///=> Filter will work on api call.
///=> User can only select value from api filtered options.
///=> If filtered options changes, selected value will become null.
///=> Clear Icon will clear filtered search.
///=> Search Icon will filter new options.

